import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { AuthLayout } from "./Login";

/* Reset password — arrived via the Brevo email link. URL pattern:
   /reset-password/<token>. We pull the token from useParams.

   Client-side password rules mirror the server (min 10, letter+digit).
   The server is the source of truth; this is just early feedback. */
export function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const valid = pw.length >= 10 && /[A-Za-z]/.test(pw) && /\d/.test(pw);
  const matches = pw === confirm;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!valid) { setError("Password must be at least 10 characters and include a letter and a digit."); return; }
    if (!matches) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/portal-x7k3", { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't reset password. Try again or request a new link.");
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthLayout title="Password updated">
        <div className="text-center">
          <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
          <p className="text-sm text-slate-700">Your password has been reset.</p>
          <p className="text-xs text-slate-500 mt-2">Redirecting you to sign in…</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set a new password" subtitle="Choose a password you don't use anywhere else.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">New password</label>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            required
            autoFocus
            autoComplete="new-password"
            className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2.5 focus:outline-none focus:ring-blue-500"
          />
          <p className={`text-[10px] mt-1 ${valid ? "text-green-600" : "text-slate-400"}`}>
            At least 10 characters, with a letter and a digit
          </p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Confirm password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2.5 focus:outline-none focus:ring-blue-500"
          />
          {confirm && !matches && <p className="text-[10px] text-red-600 mt-1">Doesn't match</p>}
        </div>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy || !valid || !matches}
          className="w-full bg-[#162a52] hover:bg-[#0f1f3d] disabled:opacity-60 text-white font-semibold rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {busy ? "Saving…" : "Set password"}
        </button>
        <div className="text-center">
          <Link to="/portal-x7k3" className="text-xs text-blue-600 hover:text-blue-800">Back to sign in</Link>
        </div>
      </form>
    </AuthLayout>
  );
}
export default ResetPassword;
